import * as React from 'react';
import { toast } from 'react-toastify';
import { httpGet, httpPost, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { GridRowModes, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid';
import DoneIcon from '@mui/icons-material/Done';
import CancelIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import Zoom from '@mui/material/Zoom';
import { VendorLookup } from '../Lookups/VendorLookup';
import { PayTo } from './PayTo';
import { useCallback } from 'react';
import { getDisplayName } from '../Common/Utilities';
import { getFilter, setFilter } from '../Common/Utilities';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom in={true} ref={ref} {...props} />;
});

export default function VendorPayToMapping(props) {

    const apiGridRef = useGridApiRef();
    const userInfo = JSON.parse(localStorage.userInfo);
    const [filterText, setFilterText] = React.useState(getFilter(["SystemSetup", "VendorSiteMapping", "searchText"]) || '')
    const [sortModel, setSortModel] = React.useState(getFilter(["SystemSetup", "VendorSiteMapping", "sortModel"]) || [])
    const [data, setData] = React.useState([]);
    const [inProgress, setInProgress] = React.useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
    const [rowModesModel, setRowModesModel] = React.useState({});
    const [currentEditRowId, setCurrentEditRowId] = React.useState(null);
    const [isAdding, setIsAdding] = React.useState(null);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = React.useState(false);
    const [deleteRowData, setDeleteRowData] = React.useState(null);
    const [isVendorDialogOpen, setIsVendorDialogOpen] = React.useState(false);
    const [isPayToDialogOpen, setIsPayToDialogOpen] = React.useState(false);
    const [selectedVendor, setSelectedVendor] = React.useState(null);
    const [selectedPayTo, setSelectedPayTo] = React.useState(null);
    const [currentRowId, setCurrentRowId] = React.useState(null);
    const [currentColumn, setCurrentColumn] = React.useState(null);
    const [vendorDialogSource, setVendorDialogSource] = React.useState(null);
    const [originalData, setOriginalData] = React.useState({});
    const [initialData, setInitialData] = React.useState([]);

    const fetchVendorPayToMappings = useCallback(() => {
        props.inProgress(true);
        const url = '/api/setup/vendorpaytomapping';
        httpGet(url)
            .then((response) => {
                const fetchedData = response.data.records.map((row, index) => ({
                    ...row,
                    rowId: `${row.vendorCode}-${row.siteId}-${index}`,
                }));
                setData(fetchedData);
                setInitialData(fetchedData);
                props.inProgress(false);
            })
            .catch((error) => {
                toast.error('Failed to fetch data');
                props.inProgress(false);
            });
    }, [props]);

    const getAddBoxButton = () => (
        <IconButton
            variant="contained"
            onClick={handleAddClick}
            size="small"
            style={{ float: "right", zIndex: 1, marginTop: 10, display: 'block' }}
            disabled={isButtonDisabled}
        >
            <Tooltip title={'Add'}><AddBoxIcon /></Tooltip>
        </IconButton>
    );

    const handleAddClick = useCallback(() => {
        const id = ([...data]?.sort((a, b) => b.rowId - a.rowId)[0]?.rowId + 1) || 1;
        let model = {
            rowId: id,
            isNew: true,
            vendorCode: '',
            vendorName: '',
            siteId: '',
            siteName: ''
        };
        setData((prevData) => [model, ...prevData]);
        setCurrentEditRowId(id);
        setRowModesModel((prevModel) => ({ ...prevModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'vendorCode' } }));
        setIsButtonDisabled(true);
        setIsAdding(true);
        setSelectedVendor(null);
        setSelectedPayTo(null);
    }, [data]);

    const handleEditClick = useCallback((id) => {
        const originalRow = initialData.find(row => row.rowId === id);
        setOriginalData((prev) => ({ ...prev, [id]: { ...originalRow } }));
        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: { mode: GridRowModes.Edit }
        }));
        setCurrentEditRowId(id);
        setIsButtonDisabled(true);
        setIsAdding(false);
    }, [initialData]);

    const handleCancelClick = useCallback((id) => {
        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));

        setData((prevData) => prevData.map((row) => {
            if (row.rowId === id && !originalData[id]) {
                return null;
            }
            return row.rowId === id ? originalData[id] || row : row;
        }).filter(row => row !== null));

        setCurrentEditRowId(null);
        setSelectedPayTo(null);
        setSelectedVendor(null);
        setIsButtonDisabled(false);
        setIsAdding(false);
    }, [originalData]);


    const handleSaveClick = (newData) => {
        const updatedData = {
            ...newData,
            vendorCode: selectedVendor?.vendorCode || newData.vendorCode,
            vendorName: selectedVendor?.vendorName || newData.vendorName,
            siteId: selectedPayTo?.siteId || newData.siteId,
            siteName: selectedPayTo?.siteName || newData.siteName,
        };

        setRowModesModel((prevModel) => ({ ...prevModel, [updatedData.rowId]: { mode: GridRowModes.View } }));
    };

    const handleDeleteClick = React.useCallback((rowData) => {
        setDeleteRowData(rowData);
        setOpenDeleteConfirmation(true);
    }, []);

    const closeDeleteDialog = React.useCallback(() => {
        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);
    }, []);

    const deleteVendorPayToMapping = React.useCallback(() => {
        props.inProgress(true);
        const data = deleteRowData;
        const url = `/api/setup/deleteVendorPayToMapping?vendorCode=${encodeURIComponent(data.vendorCode)}&siteId=${encodeURIComponent(data.siteId)}`;

        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);

        httpDelete(url)
            .then((response) => {
                props.inProgress(false);
                toast.success('Deleted Successfully');
                fetchVendorPayToMappings();
            })
            .catch((error) => {
                props.inProgress(false);
                toast.error('Delete Failed');
            });
    }, [deleteRowData]);


    const saveVendorPayToMapping = React.useCallback((newData, oldData) => {
        let rowData = structuredClone(newData);
        let endpoint = "/api/setup/saveVendorPayToMapping";

        props.inProgress(true);

        httpPost(endpoint, rowData)
            .then((response) => {
                props.inProgress(false);
                toast.success('Added Successfully');
                fetchVendorPayToMappings();
                setIsButtonDisabled(false);
            })
            .catch((error) => {
                if (newData?.isNew) {
                    setData((prev) => prev.filter(e => e.rowId !== newData.rowId));
                } else {
                    setData((prev) => prev.map(e => e.rowId === newData.rowId ? oldData : e));
                }

                props.inProgress(false);

                const errorMessage = error.response?.data || '';
                if (errorMessage.includes('Duplicate entry')) {
                    toast.error('Update Failed: Duplicate entry');
                } else {
                    toast.error(<div>Update Failed<br />{errorMessage}</div>);
                }

                setIsButtonDisabled(false);
                fetchVendorPayToMappings();
            });
    }, [fetchVendorPayToMappings, props]);

    const isRecordValid = (data, existingData) => {
        let isValid = true;
        let message = "";

        const newVendorCode = data.vendorCode?.trim().toLowerCase();
        const newSiteId = data.siteId?.trim().toLowerCase();

        const recordExists = existingData.some(row =>
            row.vendorCode.trim().toLowerCase() === newVendorCode &&
            row.siteId.trim().toLowerCase() === newSiteId &&
            row.rowId !== data.rowId
        );

        if (recordExists) {
            toast.error('This vendor code and site ID combination already exists');
            return false;
        }

        if (!data.vendorCode || data.vendorCode.trim().length === 0) {
            message += "Vendor Code, ";
        }

        if (!data.siteId || data.siteId.trim().length === 0) {
            message += "Site ID, ";
        }

        const siteIdMappedToDifferentVendor = existingData.find(row =>
            row.siteId.trim().toLowerCase() === newSiteId &&
            row.vendorCode.trim().toLowerCase() !== newVendorCode &&
            row.rowId !== data.rowId
        );

        if (siteIdMappedToDifferentVendor) {
            toast.error(`Site ${data.siteName} already mapped to ${siteIdMappedToDifferentVendor.vendorCode} - ${siteIdMappedToDifferentVendor.vendorName}.`);
            return false;
        }

        if (newVendorCode === newSiteId) {
            toast.error('Vendor and PayTo cannot be the same');
            return false;
        }

        if (message.length > 0) {
            message = message.slice(0, -2);
            toast.error(`Please provide ${message}`);
            isValid = false;
        }

        return isValid;
    };

    const processRowUpdate = React.useCallback((newRow, oldRow) => {
        const updatedRow = {
            ...newRow,
            vendorCode: selectedVendor?.vendorCode || newRow.vendorCode,
            vendorName: selectedVendor?.vendorName || newRow.vendorName,
            siteId: selectedPayTo?.siteId || newRow.siteId,
            siteName: selectedPayTo?.siteName || newRow.siteName,
        };

        if (!isRecordValid(updatedRow, initialData)) {
            setRowModesModel({ ...rowModesModel, [updatedRow.rowId]: { mode: GridRowModes.Edit } });
            throw new Error('Validation failed');
        }

        setCurrentEditRowId(null);
        saveVendorPayToMapping(updatedRow, oldRow);

        return oldRow;
    }, [saveVendorPayToMapping, selectedVendor, selectedPayTo, data]);

    React.useEffect(() => {
        const gridRef = apiGridRef?.current;
        if (gridRef && typeof gridRef.setQuickFilterValues === 'function') {
            gridRef.setQuickFilterValues([filterText]);
        }
        fetchVendorPayToMappings();
    }, []);
    
    const handleSearchClick = (rowId, column) => {
        setCurrentRowId(rowId);
        setCurrentColumn(column);
        if (column === "vendorCode") {
            setIsVendorDialogOpen(true);
            setVendorDialogSource('vendorsitemapping');
        }
        else
            setIsPayToDialogOpen(true);
    };

    const handleVendorSelect = useCallback((event, vendor) => {
        setIsVendorDialogOpen(false);
        if (!vendor) {
            return;
        }
        setSelectedVendor(vendor);
        setData((prevData) =>
            prevData.map((row) =>
                row.rowId === currentEditRowId
                    ? {
                        ...row,
                        vendorCode: vendor?.vendorCode,
                        vendorName: vendor?.vendorName,
                    }
                    : row
            )
        );
    }, [currentEditRowId]);

    const handlePayToSelect = useCallback((event, payTo) => {
        setIsPayToDialogOpen(false);

        if (!payTo) {
            return;
        }

        setSelectedPayTo(payTo);

        setData((prevData) =>
            prevData.map((row) =>
                row.rowId === currentEditRowId
                    ? {
                        ...row,
                        siteId: payTo?.siteId,
                        siteName: payTo?.siteName,
                    }
                    : row
            )
        );
    }, [currentEditRowId]);

    const renderVendorCodeCell = useCallback((params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

        React.useEffect(() => {
            if (!isInEditMode) {
                setSelectedVendor(null);
            }
        }, [isInEditMode]);

        if (params.row.isNew) {
            return (
                <div>
                    {selectedVendor?.vendorCode || params.row.vendorCode || 'Select Vendor'}
                    <IconButton size="small" onClick={() => handleSearchClick(params.id, 'vendorCode')}>
                        <SearchIcon />
                    </IconButton>
                </div>
            );
        } else if (isInEditMode) {
            return (
                <div>
                    {selectedVendor?.vendorCode || params.row.vendorCode }
                    <IconButton size="small" onClick={() => handleSearchClick(params.id, 'vendorCode')}>
                        <SearchIcon />
                    </IconButton>
                </div>
            );
        }

        return params.value;
    }, [rowModesModel, handleSearchClick, selectedVendor]);

    const renderVendorNameCell = useCallback((params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

        React.useEffect(() => {
            if (!isInEditMode) {
                setSelectedVendor(null);
            }
        }, [isInEditMode]);

        if (params.row.isNew) {
            return (
                <div>
                    {selectedVendor?.vendorName || params.row.vendorName || ''}
                </div>
            );
        } else if (isInEditMode) {
            return (
                <div>
                    {selectedVendor?.vendorName || params.row.vendorName }
                </div>
            );
        }

        return params.value;
    }, [rowModesModel, selectedVendor]);

    const renderPayToCell = useCallback((params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        const siteDisplayName = getDisplayName('site');

        React.useEffect(() => {
            if (!isInEditMode) {
                setSelectedPayTo(null);
            }
        }, [isInEditMode]);

        if (params.row.isNew) {
            return (
                <div>
                    {selectedPayTo?.siteId || params.row.siteId || `Select ${siteDisplayName}`}
                    <IconButton size="small" onClick={() => handleSearchClick(params.id, 'siteId')}>
                        <SearchIcon />
                    </IconButton>
                </div>
            );
        } else if (isInEditMode) {
            return (
                <div>
                    {selectedPayTo?.siteId || params.row.siteId || `Select ${siteDisplayName}`}
                    <IconButton size="small" onClick={() => handleSearchClick(params.id, 'siteId')}>
                        <SearchIcon />
                    </IconButton>
                </div>
            );
        }

        return params.value;
    }, [rowModesModel, handleSearchClick, selectedPayTo]);

    const renderPayToNameCell = useCallback((params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

        React.useEffect(() => {
            if (!isInEditMode) {
                setSelectedPayTo(null);
            }
        }, [isInEditMode]);

        if (params.row.isNew) {
            return (
                <div>
                    {selectedPayTo?.siteName || params.row.siteName || ''}
                </div>
            );
        } else if (isInEditMode) {
            return (
                <div>
                    {selectedPayTo?.siteName || params.row.siteName || ''}
                </div>
            );
        }

        return params.value;
    }, [rowModesModel, selectedPayTo]);
    
    const vendorLookupInfo = React.useMemo(() => {
        return {
            openDialog: isVendorDialogOpen,
            invoiceModel: { vendorCode: '', siteId: '', currencyCode: '', companyCode: userInfo.companyCode},
            inProgress: props.inProgress,
            source: vendorDialogSource,
        };
    }, [isVendorDialogOpen]);

    const payToLookupInfo = React.useMemo(() => {
        return {
            openDialog: isPayToDialogOpen,
            invoiceModel: { vendorCode: '', siteId: '', currencyCode: '' },
            inProgress: props.inProgress,
        };
    }, [isPayToDialogOpen]);

    return (
        <div>
            <h1>Vendor {getDisplayName('site')} Mapping</h1>
            <div className="form-group">
                <div style={{ display: 'block', overflow: 'auto', width: '100%' }}>
                    <EnhancedDataGrid
                        rows={data}
                        disableColumnMenu
                        pagination
                        editMode="row"
                        pageSize={10}
                        apiRef={apiGridRef}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(err) => { }}
                        rowModesModel={rowModesModel}
                        setFilterText={(text) => {
                            const trimmed = text?.trim() || '';
                            setFilterText(trimmed);
                            setFilter(['SystemSetup', 'VendorSiteMapping', 'searchText'], trimmed);
                        }}
                        filterText={filterText}
                        onSortModelChange={(model) => {
                            setSortModel(model);
                            setFilter(['SystemSetup', 'VendorSiteMapping', 'sortModel'], model);
                        }}
                        sortModel={sortModel}
                        columns={[
                            {
                                headerName: 'Vendor Code',
                                field: 'vendorCode',
                                flex: 0.7,
                                editable: isAdding,
                                renderCell: renderVendorCodeCell,
                                renderEditCell: renderVendorCodeCell,
                            },
                            {
                                headerName: 'Vendor Name',
                                field: 'vendorName',
                                flex: 1,
                                editable: true,
                                renderCell: renderVendorNameCell,
                                renderEditCell: renderVendorNameCell,
                            },
                            {
                                headerName: getDisplayName('site') + ' Id',
                                field: 'siteId',
                                flex: 0.7,
                                editable: true,
                                renderCell: renderPayToCell,
                                renderEditCell: renderPayToCell,
                            },
                            {
                                headerName: getDisplayName('site') + ' Name',
                                field: 'siteName',
                                flex: 1,
                                editable: true,
                                renderCell: renderPayToNameCell,
                                renderEditCell: renderPayToNameCell,
                            },
                            {
                                field: 'actions',
                                type: 'actions',
                                headerName: 'Actions',
                                width: 150,
                                cellClassName: 'actions',
                                getActions: (rowData) => {
                                    const isInEditMode = rowModesModel[rowData.id]?.mode === GridRowModes.Edit;

                                    if (isInEditMode) {
                                        return [
                                            <GridActionsCellItem
                                                key={rowData.id + 1}
                                                icon={<Tooltip title={'Save'}><DoneIcon /></Tooltip>}
                                                label="Save"
                                                color="inherit"
                                                onClick={() => { handleSaveClick(rowData.row) }}
                                            />,
                                            <GridActionsCellItem
                                                key={rowData.id + 2}
                                                icon={<Tooltip title={'Cancel'}><CancelIcon /></Tooltip>}
                                                label="Cancel"
                                                className="textPrimary"
                                                onClick={() => { handleCancelClick(rowData.id) }}
                                                color="inherit"
                                            />,
                                        ];
                                    }

                                    return [
                                        <GridActionsCellItem
                                            key={rowData.id + 2}
                                            icon={<Tooltip title={'Edit'}><EditIcon /></Tooltip>}
                                            label="Edit"
                                            className="textPrimary"
                                            onClick={() => { handleEditClick(rowData.id) }}
                                            color="inherit"
                                            disabled={isButtonDisabled}
                                        />,
                                        <GridActionsCellItem
                                            key={rowData.id + 3}
                                            icon={<Tooltip title={'Delete'}><DeleteIcon /></Tooltip>}
                                            label="Delete"
                                            onClick={() => { handleDeleteClick(rowData.row) }}
                                            color="inherit"
                                            disabled={isButtonDisabled}
                                        />,
                                    ];
                                }
                            }
                        ]}
                        getRowId={(row) => row.rowId} // Specify custom row ID
                        customcomponent={
                            <>
                                {getAddBoxButton()}
                            </>
                        }
                    />
                </div>
            </div>
            <VendorLookup
                info={vendorLookupInfo}
                onClose={handleVendorSelect}
                hideSiteId={true}
            />
            <PayTo
                info={payToLookupInfo}
                onClose={handlePayToSelect}
            />
            <Dialog
                open={openDeleteConfirmation}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    Delete Record
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this mapping?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteVendorPayToMapping} color="primary">
                        Yes
                    </Button>
                    <Button autoFocus onClick={closeDeleteDialog} color="primary">
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}